import React, {ReactNode, Ref, useEffect, useState} from 'react';
import {Observable, of} from 'rxjs';
import {isFunction} from 'underscore';
import {DetailedProps} from '@esgi/ui-kit/utils';
import FormElementContext, {FormElementContextState} from './form-element/form-element-context';
import {ElementStatus, ValidationResult, Validator, ValidatorArgs} from './types';
import {map} from 'rxjs/operators';

interface Props<T> extends Omit<DetailedProps<HTMLDivElement>, 'children'> {
	ref?: Ref<HTMLDivElement>;
	value: T;
	setValue: (value: T) => void;
	validators: Validator<T>[];
	additionalErrors?: string[];
	validateOnChange?: boolean;
	validateOnInit?: boolean;
	children: ReactNode | Function
}

export function ValidatedElement<T>({
	                                    setValue,
	                                    value,
	                                    validators,
	                                    additionalErrors = [],
	                                    onBlur,
	                                    validateOnChange,
	                                    validateOnInit = false,
	                                    children,
	                                    ...divProps
}: Props<T>): JSX.Element {
	const [lastErrors, setLastErrors] = useState<ValidationResult>([]);
	const [status, setStatus] = useState(ElementStatus.untouched);
	const [ref, setRef] = useState();

	function hasError(error?: string): boolean {
		if (!error) {
			return !!lastErrors.length;
		}
		return lastErrors.map(v => v.toLowerCase()).indexOf(error.toLowerCase()) > -1;
	}

	useEffect(() => {
		if (validateOnInit) {
			validate(false).subscribe();
		}
	}, []);

	useEffect(() => {
		if (validateOnChange) {
			validate(false).subscribe();
		}
	}, [validateOnChange, value]);

	useEffect(() => {
		if (additionalErrors.length) {
			validate(false).subscribe();
		}
	}, [additionalErrors]);

	const elementContext = {
		value,
		status,
		validate,
		hasError,
		setStatus,
		setValue,
		setRef,
		firstError: () => lastErrors[0],
		validateOnBlur: () => {
			validate(false).subscribe();
		},
		ref,
	} as FormElementContextState;

	function validate(silence?: boolean): Observable<ValidationResult> {
		if (!validators.length) {
			setStatus(ElementStatus.valid);
			return of([]);
		}

		if (status === ElementStatus.disabled) {
			return of([]);
		}

		setStatus(ElementStatus.pending);

		return new Observable<ValidationResult>(subscriber => {
			let cancelled = false;
			let inner = new Observable<ValidatorArgs<T>>(innerSubscriber => {
				innerSubscriber.next({
					field: {
						currentValue: value,
					},
					errors: [],
					cancelled: false,
				});
			});
			for (const validator of validators) {
				inner = inner.pipe(map(v => {
					if (cancelled) {
						throw 'cancelled';
					} else {
						return v;
					}
				}));
				inner = validator(inner);
			}

			const subscribe = inner.subscribe(r => {
				const errors = [...r.errors, ...additionalErrors];
				setLastErrors(errors);
				if (!silence) {
					setStatus(errors.length ? ElementStatus.invalid : ElementStatus.valid);
				}
				subscriber.next(errors);
				subscriber.complete();
			});
			return {
				unsubscribe() {
					cancelled = true;
					subscribe.unsubscribe();
				},
			};
		});
	}


	return <FormElementContext.Provider value={elementContext}>
		<div {...divProps}>
			{isFunction(children) ? children(elementContext) : children}
		</div>
	</FormElementContext.Provider>;
}
